import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  filter,
  map,
  switchMap,
  catchError,
  first,
  distinctUntilChanged,
  tap,
  delay
} from 'rxjs/operators';
import {
  SetUser,
  SendVerificationEmail,
  UserActionTypes,
  UserEffectsInit
} from '../actions/user.actions';
import {
  of,
  Observable,
  combineLatest,
  defer
} from 'rxjs';
import { DigiAIXCoreAppState } from '../../app-state';
import { Store, select } from '@ngrx/store';
import { getRouterState } from '../selectors/auth.selectors';
import {
  ShowInfoMessage,
  ShowErrorMessage
} from '../../notification/notification.actions';
import {
  requireVerifiedEmail,
  isAuthEnabled
} from '../../config/config.selectors';
import { AuthService } from '../auth.service';
import { ConfigService } from '../../config/config.service';
import {
  AppNavigateTo,
  LifeCycleActionTypes,
  AppAuthenticated,
  AppUnauthenticated
} from '../../life-cycle/life-cycle.actions';
import { ClearAll } from '../../documents/document.actions';
import { NGXLogger } from 'ngx-logger';

@Injectable()
export class UserEffects<AppState extends DigiAIXCoreAppState> {

  @Effect()
  public init$ = defer(() => of(new UserEffectsInit()).pipe(
    tap(() => this.logger.info('[UserEffects::init$]')),
  ));

  @Effect({ resubscribeOnError: true })
  public setUser$ = this.actions$.pipe(
    ofType<AppAuthenticated>(LifeCycleActionTypes.AppAuthenticated),
    switchMap(() =>
      this.store$.pipe(
        select(getRouterState),
        first(),
        map(routerState => {
          if (
            routerState &&
            routerState.url &&
            !routerState.url.match(/^\/auth/)
          ) {
            return new AppNavigateTo(
              [routerState.url],
              'UserEffects::setUser$::redirect',
              {
                queryParams: routerState.queryParams || {}
              }
            );
          } else {
            return new AppNavigateTo(['/'], 'UserEffects::setUser$::base');
          }
        })
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public resetUser$ = this.actions$.pipe(
    ofType<AppUnauthenticated>(LifeCycleActionTypes.AppUnauthenticated),
    switchMap(() =>
      this.store$.pipe(
        select(getRouterState),
        first(),
        switchMap(routerState => {
          if (
            routerState &&
            routerState.url &&
            routerState.url.match(/^\/auth/)
          ) {
            return [
              new AppNavigateTo([routerState.url], 'UserEffects::resetUser$', {
                queryParams: routerState.queryParams || {}
              })
            ];
          } else {
            return [
              new AppNavigateTo(['/', 'auth'], 'UserEffects::resetUser$'),
              new ClearAll([], false)
            ];
          }
        })
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public isEmailVerified$ = this.config.whenAuthEnabled(() =>
    this.store$.select(requireVerifiedEmail).pipe(
      first(),
      filter(verifyEmail => !!verifyEmail),
      switchMap(() =>
        this.auth.user.pipe(
          filter(user => user && user.emailVerified),
          map(
            () =>
              new AppNavigateTo(
                ['/', 'auth', 'email-verification'],
                'UserEffects::isEmailVerified$'
              )
          )
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public sendVerificationEmail = this.actions$.pipe(
    ofType<SendVerificationEmail>(UserActionTypes.SendVerificationEmail),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(() =>
      this.auth.sendEmailVerification().pipe(
        map(() => new ShowInfoMessage('Email verification link is send')),
        catchError(err => of(new ShowErrorMessage(err)))
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public userChange$: Observable<SetUser> = combineLatest(
    this.auth.user.pipe(
      tap(user => this.logger.debug('[UserEffects::userChange$] auth.user', user)),
      distinctUntilChanged()
    ),
    this.config.authIsEnabled$.pipe(
      tap(authIsEnabled => this.logger.debug('[UserEffects::userChange$] config.authIsEnabled', authIsEnabled))
    )
  ).pipe(map(([user]) => new SetUser(user)));

  constructor(
    private actions$: Actions,
    private auth: AuthService,
    private store$: Store<AppState>,
    private config: ConfigService,
    private logger: NGXLogger
  ) {}
}
